/**
 * @file vuexstore to handle draft posts
 * Draft posts are show in the post modal but not saved to the server yet
 * This module is dependent on the post module to get cid of the post being edited
 */

import { trackEvent } from '@@/bits/analytics'
import { partitionedDebounce } from '@@/bits/partitioned_debounce'
import { shouldSyncDraftUpdates } from '@@/bits/post_drafts'
import PromiseQueue from '@@/bits/promise_queue'
import { useNativeAppStore } from '@@/pinia/native_app'
import PadletApi from '@@/surface/padlet_api'
import { usePostBuilder } from '@@/vuecomposables/surface_post_builder'
import { __ } from 'bits/intl'
import { POST_BODY_CHARACTER_LIMIT, POST_SUBJECT_CHARACTER_LIMIT } from 'bits/numbers'
import { vDel, vSet } from 'bits/vue'
import { SnackbarNotificationType } from 'enums'
import { sortBy } from 'lodash-es'
import type { AttachmentProps, Cid, DraftPost, Id, Post, PostAttributes, Section } from 'types'
import type { Dispatch, Module } from 'vuex'
import { isEmpty } from 'vuexstore/helpers/post'
import type { RootState } from 'vuexstore/surface/types'

export enum DraftSaveState {
  Unsaved = 'unsaved',
  Saved = 'saved',
  Creating = 'creating',
  Updating = 'updating',
  Deleting = 'deleting',
}

function autoSaveQueueKey(cid: Cid): string {
  return `syncDraft:${cid}`
}

interface DraftState {
  draftByCid: Record<Cid, DraftPost>
  // when a minimized draft has no subject, we will use "Draft 1", "Draft 2", ... as its title,
  // this counter is to keep track of the number to be used
  minimizedDraftPlaceholderTitleCount: number
  draftShowingMinimizedContextMenuCid: Cid | null
  // when a single file/text/link is dropped/pasted on the padlet page, we will open a new docked draft
  // if we are in shelf format, the draft will go to "pending mode" and we have to open the section picker to select a column first
  // => store the new draft's cid here so we can open it later once a column is selected
  latestPendingDraftCid: Cid | null

  // state supporting post draft autosave. Also includes DRAFT_SYNC_DEBOUNCE_TIMERS below.
  autoSaveQueueByCid: Record<Cid, PromiseQueue | undefined> // promise queue for draft autosave. one per draft.
  draftSyncState: {
    [cid: Cid]:
      | {
          state: DraftSaveState
          loading: boolean
          error: boolean
        }
      | undefined
  }
}

const DRAFT_SYNC_DEBOUNCE_TIMERS = new Map<string, any>()
function clearDraftSyncDebounceTimer(cid: Cid): void {
  clearTimeout(DRAFT_SYNC_DEBOUNCE_TIMERS.get(cid))
}

const initialState = (): DraftState => {
  return {
    draftByCid: {},
    minimizedDraftPlaceholderTitleCount: 0,
    draftShowingMinimizedContextMenuCid: null,
    latestPendingDraftCid: null,
    autoSaveQueueByCid: {},
    draftSyncState: {},
  }
}

const draft: Module<DraftState, RootState> = {
  namespaced: true,
  state: initialState,
  getters: {
    draftByCid:
      (state): Function =>
      (cid: Cid | null | undefined): DraftPost | undefined =>
        cid ? state.draftByCid[cid] : undefined,
    draftCids: (state, getters): Cid[] =>
      sortBy(Object.keys(state.draftByCid), (postCid) => -1 * getters.draftByCid(postCid).draftCreatedAt),
    // Draft posts that are not being edited, i.e. minimized
    standByDraftCids: (state, getters, _, rootGetters): Cid[] =>
      getters.draftCids.filter((postCid) => postCid !== rootGetters['post/postBeingEditedCid']),
    // The draft post being edited
    activeDraft: (state, getters, _, rootGetters): DraftPost | null => {
      return rootGetters['post/postBeingEditedCid'] ? getters.draftByCid(rootGetters['post/postBeingEditedCid']) : null
    },
    activeDraftCid: (state, getters): Cid | null => getters.activeDraft?.cid,
    getAttachmentPropsFromDraft:
      (_, getters): Function =>
      (cid: Cid): AttachmentProps | null =>
        getters.draftByCid(cid)?.wish_content?.attachment_props ?? null,
    isAnyDraftActive: (state, getters): boolean => !!getters.activeDraftCid,
    isCidActiveDraft:
      (state, getters): Function =>
      (cid: Cid): boolean =>
        !!cid && getters.activeDraftCid === cid,
    isCidBeingDrafted:
      (state, getters): Function =>
      (cid: Cid): boolean =>
        !!getters.draftByCid(cid),
    activeDraftUploadingFile: (state, getters): File | undefined | null => getters.activeDraft?.uploadingFile,
    draftShowingMinimizedContextMenuCid: (state): Cid | null => state.draftShowingMinimizedContextMenuCid,
    isDraftAutoSavePending:
      (state) =>
      (cid: Cid): boolean => {
        // pending debounced sync
        if (DRAFT_SYNC_DEBOUNCE_TIMERS.has(cid)) {
          return true
        }

        // promise queue is not empty
        const autoSaveQueue = state.autoSaveQueueByCid[cid]
        if (autoSaveQueue?.isEmpty === false) {
          return true
        }

        return false
      },
  },
  mutations: {
    UPDATE_DRAFT(state, post: DraftPost): void {
      vSet(state.draftByCid, post.cid, post)
    },
    REMOVE_DRAFT(state, postCid: Cid): void {
      vDel(state.draftByCid, postCid)
    },
    SAVE_DRAFT_PLACEHOLDER_TITLE(state, postCid: Cid): void {
      state.minimizedDraftPlaceholderTitleCount += 1
      const post = state.draftByCid[postCid]
      vSet(state.draftByCid, post.cid, {
        ...post,
        minimizedPlaceholderTitle: __('Draft %{draftNumber}', {
          draftNumber: state.minimizedDraftPlaceholderTitleCount,
        }),
      })
    },
    REMOVE_DRAFT_ATTACHMENT(state, { postCid }: { postCid: Cid }): void {
      if (state.draftByCid[postCid].wish_content != null) {
        vSet(state.draftByCid[postCid].wish_content as object, 'attachment_props', null)
      }
    },
    SHOW_MINIMIZED_CONTEXT_MENU(state, cid): void {
      state.draftShowingMinimizedContextMenuCid = cid
    },
    HIDE_MINIMIZED_CONTEXT_MENU(state): void {
      state.draftShowingMinimizedContextMenuCid = null
    },
    SET_LATEST_PENDING_DRAFT_CID(state, cid): void {
      state.latestPendingDraftCid = cid
    },
    AUTO_PUBLISH_DRAFT(state, cid: Cid): void {
      vSet(state.draftByCid[cid], 'shouldAutoPublish', true)
    },
    STOP_AUTO_PUBLISH_DRAFT(state, cid: Cid): void {
      vSet(state.draftByCid[cid], 'shouldAutoPublish', false)
    },
    SET_DRAFT_AUTOSAVE_QUEUE(state, cid) {
      vSet(state.autoSaveQueueByCid, cid, new PromiseQueue())
    },
    REMOVE_DRAFT_AUTOSAVE_QUEUE(state, cid) {
      vDel(state.autoSaveQueueByCid, cid)
    },
    SET_DRAFT_SYNC_STATE(
      state,
      payload: { cid: Cid; syncState: { state: DraftSaveState; loading: boolean; error: boolean } },
    ) {
      vSet(state.draftSyncState, payload.cid, payload.syncState)
    },
    CLEAR_DRAFT_SYNC_STATE(state, cid: Cid) {
      vDel(state.draftSyncState, cid)
    },
  },
  actions: {
    startNewDraft(
      { dispatch, rootGetters, rootState, commit },
      {
        attributes,
        shouldStartEditing,
      }: { attributes: Post | DraftPost | PostAttributes; shouldStartEditing: boolean },
    ): void {
      const post = usePostBuilder().buildPost(attributes)
      if (rootGetters.canUseSections) {
        if (!post.wall_section_id) post.wall_section_id = rootGetters['post/assignedWallSectionId']
      } else {
        const usableSections = rootGetters['section/usableSections'] as Section[]
        if (rootGetters.isShelf && !post.wall_section_id && usableSections.length !== 1) {
          dispatch('post/createPendingPost', { postAttributes: { ...post, file: attributes.file } }, { root: true })
          if (shouldStartEditing) {
            commit('SET_LATEST_PENDING_DRAFT_CID', post.cid)
          }
          return
        }
        if (rootGetters.isShelf && !post.wall_section_id && usableSections.length === 1) {
          post.wall_section_id = usableSections[0].id
        }
      }
      dispatch('post/updatePostInStore', { ...post, subject: '', body: '', attachment: '' }, { root: true })
      dispatch('updateDraft', post)
      dispatch('setDraftNumber', { cid: post.cid })
      if (shouldStartEditing) {
        dispatch('post/startEditingPost', { postCid: post.cid }, { root: true })
      }
      if (attributes.file != null) {
        dispatch('startUploadFileInDraft', { file: attributes.file, cid: post.cid })
      }
    },
    updateDraft({ commit, dispatch, state, rootGetters }, post: Post | DraftPost | PostAttributes): void {
      if (post.cid == null) return

      const existingDraft: DraftPost | undefined = state.draftByCid[post.cid]
      const newDraft = { ...existingDraft, ...post }
      if (newDraft.draftCreatedAt == null) {
        newDraft.draftCreatedAt = Date.now()
      }

      // reset attachment caption when attachment changed
      if (
        existingDraft?.attachment != null &&
        post.attachment != null &&
        post.attachment !== existingDraft.attachment
      ) {
        newDraft.attachment_caption = null
      }
      commit('UPDATE_DRAFT', newDraft)

      // Draft autosave start

      if (
        rootGetters.isSubmissionRequest === true || // Don't save draft when making submission request
        rootGetters['post/isExistingPost'](newDraft.cid) === true || // draft of existing post, don't need to autosave
        rootGetters.amIRegistered === false // Only registered users get saved drafts
      ) {
        return
      }

      if (state.autoSaveQueueByCid[newDraft.cid] == null) {
        commit('SET_DRAFT_AUTOSAVE_QUEUE', newDraft.cid)
      }

      if (state.draftSyncState[newDraft.cid] == null) {
        commit('SET_DRAFT_SYNC_STATE', {
          cid: newDraft.cid,
          syncState: {
            state: newDraft.id == null ? DraftSaveState.Unsaved : DraftSaveState.Saved,
            loading: false,
            error: false,
          },
        })
      }

      if (
        shouldSyncDraftUpdates({
          newDraft,
          existingDraft,
        })
      ) {
        void dispatch('debouncedSyncDraft', newDraft)
      }

      // We also need to send surface state when performing autosave
      void useNativeAppStore().postSurfaceState()
      // Autosave end
    },
    updateActiveDraft({ dispatch, getters }, post: Post | DraftPost | PostAttributes): void {
      void dispatch('updateDraft', { ...getters.activeDraft, ...post })
    },
    async removeDraft({ commit, dispatch, getters, state, rootGetters }, postCid: Cid): Promise<void> {
      if (postCid === getters.activeDraftCid) {
        void dispatch('stopUploadFileActiveDraft')
        void dispatch('post/stopEditingPost', null, { root: true })
      }

      if (
        rootGetters['post/isExistingPost'](postCid) === true || // removing a draft of an existing post -> it's not a saved draft, simply remove it from drafts store
        rootGetters.amIRegistered === false || // Only registered users get saved drafts
        rootGetters.isSubmissionRequest === true // we don't save drafts if is submission request
      ) {
        commit('REMOVE_DRAFT', postCid)
        return
      }

      // Autosave start
      // a previous operation (create/update) may be debounced, enqueued or executing.
      // clear debounce and queue, then enqueue a delete operation which deletes the draft and clears autosave state
      clearDraftSyncDebounceTimer(postCid)
      const autoSaveQueue = state.autoSaveQueueByCid[postCid]
      autoSaveQueue?.clear()
      await autoSaveQueue?.enqueue(autoSaveQueueKey(postCid), async function deletePostDraft() {
        const draft = state.draftByCid[postCid]
        if (draft.id != null) {
          void PadletApi.WishDraft.delete(draft.id)
        }
        commit('REMOVE_DRAFT_AUTOSAVE_QUEUE', postCid)
        commit('CLEAR_DRAFT_SYNC_STATE', postCid)
        commit('REMOVE_DRAFT', postCid)
        void useNativeAppStore().postSurfaceState()
      })
    },
    setDraftNumber({ commit, getters }, { cid }: { cid: Cid | null | undefined }): void {
      // set the title for minimized draft to "Draft 1", "Draft 2", ... if necessary
      const draftPost = cid ? getters.draftByCid(cid) : null
      if (draftPost && !draftPost.subject?.trim() && !draftPost.minimizedPlaceholderTitle) {
        commit('SAVE_DRAFT_PLACEHOLDER_TITLE', cid)
      }
    },
    updateDraftWishContentAttachmentProps(
      { dispatch, getters },
      { cid, url, attachmentProps }: { cid: Cid; url: string; attachmentProps?: AttachmentProps },
    ): void {
      if (!getters.isCidBeingDrafted(cid)) return
      if (attachmentProps != null) {
        dispatch('updateDraft', {
          cid,
          wish_content: {
            is_processed: true,
            attachment_props: attachmentProps,
          },
        })
      } else {
        dispatch('updateDraft', {
          cid,
          wish_content: {
            is_processed: false,
            attachment_props: { url },
          },
        })
      }
    },
    createNewDraftFromPendingUpload(
      { dispatch, rootState, rootGetters, state, commit },
      { file, postAttributes }: { file: File; postAttributes: Post },
    ): void {
      // Create the post inside post module
      const newPostAttributes = usePostBuilder().buildPost(postAttributes)

      dispatch('post/updatePostInStore', { ...newPostAttributes, subject: '' }, { root: true })

      // Create the draft
      dispatch('updateDraft', { ...newPostAttributes, subject: postAttributes.subject || '' })
      dispatch('setDraftNumber', { cid: newPostAttributes.cid })

      // Start upload
      dispatch('startUploadFileInDraft', { file, cid: newPostAttributes.cid })

      if (newPostAttributes.cid === state.latestPendingDraftCid) {
        dispatch('post/startEditingPost', { postCid: newPostAttributes.cid }, { root: true })
        commit('SET_LATEST_PENDING_DRAFT_CID', null)
      }
    },
    removeDraftAttachment({ commit, dispatch, state, getters }, { postCid }: { postCid: Cid }): void {
      if (state.draftByCid[postCid] != null) {
        void dispatch('updateDraft', { cid: postCid, attachment: '', attachment_caption: null })
        commit('REMOVE_DRAFT_ATTACHMENT', { postCid })
      }
    },
    // if there is not custom maxFileUploadSize, the default limit based on user membership is used
    startUploadFileInDraft(
      { dispatch },
      { file, maxFileUploadSize, cid }: { file: File; maxFileUploadSize?: number; cid: Cid },
    ): void {
      dispatch('updateDraft', { cid, maxFileUploadSize, uploadingFile: file, uploadProgress: 0 })
    },
    stopUploadFileInDraft({ dispatch }, { cid }: { cid: Cid }): void {
      dispatch('updateDraft', { cid, uploadingFile: null })
    },
    uploadFileActiveDraft(
      { dispatch, getters },
      { file, maxFileUploadSize }: { file: File; maxFileUploadSize?: number },
    ): void {
      dispatch('startUploadFileInDraft', { file, maxFileUploadSize, cid: getters.activeDraftCid })
    },
    stopUploadFileActiveDraft({ dispatch, getters }): void {
      dispatch('stopUploadFileInDraft', { cid: getters.activeDraftCid })
    },
    reassignSections({ state, dispatch }, { oldSectionId, newSectionId }: { oldSectionId: Id; newSectionId: Id }) {
      const draftsWithOldSection = Object.values(state.draftByCid).filter(
        (draft) => draft.wall_section_id === oldSectionId,
      )
      draftsWithOldSection.forEach((draft) => {
        void dispatch('updateDraft', {
          cid: draft.cid,
          wall_section_id: newSectionId,
        })
      })
    },
    async replaceActiveDraftUploadingFile({ dispatch, getters }, { file }: { file: File }): Promise<void> {
      if (getters.activeDraft?.attachment) {
        // The previous attachment upload/adding has finished, remove the existing attachment
        dispatch('removeDraftAttachment', { postCid: getters.activeDraft.cid })
      } else if (getters.activeDraftUploadingFile) {
        // Another file is being uploaded, stop that upload first
        // need await to ensure that stop upload and start new upload happens sequentially
        await dispatch('stopUploadFileActiveDraft')
      }

      dispatch('uploadFileActiveDraft', { file })
    },
    async replaceActiveDraftAttachmentWithLink({ dispatch, getters }, { link }: { link: string }): Promise<void> {
      // replace the active draft's attachment/uploading file when a link is dropped on to it
      if (getters.activeDraft?.attachment) {
        // An attachment already exists or a file upload is done => remove it
        // await to make sure the attachment removal and adding the new attachment happens sequentially
        await dispatch('removeDraftAttachment', { postCid: getters.activeDraft.cid })
      } else if (getters.activeDraftUploadingFile) {
        // A file is being uploaded, stop that upload first
        // await to ensure that stop upload and adding the new attachment happens sequentially
        await dispatch('stopUploadFileActiveDraft')
      }

      dispatch('updateActiveDraft', {
        attachment: link,
        wish_content: {
          attachment_props: {
            url: link,
          },
          is_processed: false,
        },
      })
    },
    openMinimizedContextMenu({ commit }, { cid }): void {
      commit('SHOW_MINIMIZED_CONTEXT_MENU', cid)
    },
    closeMinimizedContextMenu({ commit }): void {
      commit('HIDE_MINIMIZED_CONTEXT_MENU')
    },
    autoPublishDraft({ commit }, { cid }): void {
      commit('AUTO_PUBLISH_DRAFT', cid)
    },
    stopAutoPublishDraft({ commit }, { cid }): void {
      commit('STOP_AUTO_PUBLISH_DRAFT', cid)
    },
    publishDraft({ dispatch, rootGetters, state }, { cid }): void {
      const existingDraft = state.draftByCid[cid]
      if (existingDraft == null) return

      if (rootGetters.isPostable === false) return

      const existingPost: Post | undefined = rootGetters['post/getPostByCid'](cid)

      /**
       * We compare existingDraft with existingPost so only posts that already exceed character limit
       * can continue to exceed character limit
       */
      if (
        (existingPost?.subject == null || existingPost?.subject.length <= POST_SUBJECT_CHARACTER_LIMIT) &&
        existingDraft.subject != null &&
        existingDraft.subject.length > POST_SUBJECT_CHARACTER_LIMIT
      ) {
        void dispatch(
          'globalSnackbar/setSnackbar',
          {
            message: __('Post subject character limit reached'),
            notificationType: SnackbarNotificationType.error,
          },
          { root: true },
        )
        return
      }
      if (
        (existingPost?.body == null || existingPost?.body.length <= POST_BODY_CHARACTER_LIMIT) &&
        existingDraft.body != null &&
        existingDraft.body.length > POST_BODY_CHARACTER_LIMIT
      ) {
        void dispatch(
          'globalSnackbar/setSnackbar',
          {
            message: __('Post body character limit reached'),
            notificationType: SnackbarNotificationType.error,
          },
          { root: true },
        )
        return
      }

      // If a file is being uploaded in the draft, set it to auto publish
      if (existingDraft.uploadingFile != null) {
        void dispatch('autoPublishDraft', { cid })
      } else if (!isEmpty(existingDraft)) {
        void dispatch('post/savePost', { attributes: existingDraft }, { root: true })
      } else {
        void dispatch('post/deletePost', { postCid: cid }, { root: true })
      }
    },
    deleteAllDrafts({ dispatch, getters }): void {
      getters.draftCids.forEach((cid) => {
        dispatch('post/deletePost', { postCid: cid }, { root: true })
      })
    },
    // State machine: https://whimsical.com/post-drafts-diagrams-QVWje6U8d4TMm9xGhbDfnK@7YNFXnKbZAHvWYYvTex5e
    // This should allow us to handle some edge cases,
    // Eg: if user makes updates while the draft is still being created,
    // this makes sure the next queued operation is an update instead of another create.
    // Similarly if the draft is being deleted and the user makes update to the draft,
    // then the next enqueued operation would be a create.
    async syncDraft({ state, dispatch, commit }, draft: DraftPost) {
      const syncState = state.draftSyncState[draft.cid]
      if (syncState == null) return

      const draftSaverQueue = state.autoSaveQueueByCid[draft.cid]
      if (draftSaverQueue == null) return

      switch (syncState.state) {
        case 'unsaved':
        case 'deleting': {
          if (isEmpty(draft)) return
          await draftSaverQueue.enqueue(autoSaveQueueKey(draft.cid), async function createPostDraft() {
            const currentSyncState = state.draftSyncState[draft.cid]
            // user may have closed composer and cleared syncState/draft
            if (currentSyncState == null) return

            try {
              commit('SET_DRAFT_SYNC_STATE', {
                cid: draft.cid,
                syncState: { ...currentSyncState, state: DraftSaveState.Creating },
              })

              const attributes = await PadletApi.WishDraft.create(draft)

              // user may have closed composer and cleared syncState/draft
              if (state.draftByCid[draft.cid] == null) return

              await dispatch('updateDraft', { cid: draft.cid, id: attributes.id })
              commit('SET_DRAFT_SYNC_STATE', {
                cid: draft.cid,
                syncState: { ...currentSyncState, state: DraftSaveState.Saved },
              })

              void trackEvent('Post drafts', 'Created post draft')
            } catch (err) {
              commit('SET_DRAFT_SYNC_STATE', {
                cid: draft.cid,
                syncState: { ...currentSyncState, state: DraftSaveState.Unsaved },
              })
            }
          })
          break
        }
        case 'saved':
        case 'creating':
        case 'updating': {
          if (isEmpty(draft)) {
            draftSaverQueue.clear()
            await draftSaverQueue.enqueue(autoSaveQueueKey(draft.cid), async function deletePostDraft() {
              const currentSyncState = state.draftSyncState[draft.cid]
              // user may have closed composer and cleared syncState/draft
              if (currentSyncState == null) return

              // Draft may not have an id yet if it's still creating. Double check against more updated draft from store.
              const id = draft.id ?? state.draftByCid[draft.cid]?.id

              if (id == null) return

              try {
                commit('SET_DRAFT_SYNC_STATE', {
                  cid: draft.cid,
                  syncState: { ...currentSyncState, state: DraftSaveState.Deleting },
                })

                await PadletApi.WishDraft.delete(id)

                // user may have closed composer and cleared syncState/draft
                if (state.draftByCid[draft.cid] == null) return

                await dispatch('updateDraft', { cid: draft.cid, id: undefined })

                commit('SET_DRAFT_SYNC_STATE', {
                  cid: draft.cid,
                  syncState: { ...currentSyncState, state: DraftSaveState.Unsaved },
                })
              } catch (err) {
                commit('SET_DRAFT_SYNC_STATE', {
                  cid: draft.cid,
                  syncState: { ...currentSyncState, state: DraftSaveState.Saved },
                })
              }
            })
            break
          }

          draftSaverQueue.clear()
          await draftSaverQueue.enqueue(autoSaveQueueKey(draft.cid), async function updatePostDraft() {
            const currentSyncState = state.draftSyncState[draft.cid]
            // user may have closed composer and cleared syncState/draft
            if (currentSyncState == null) return

            // Draft may not have an id yet if it's still creating. Double check against more updated draft from store.
            const id = draft.id ?? state.draftByCid[draft.cid]?.id

            if (id == null) return

            try {
              commit('SET_DRAFT_SYNC_STATE', {
                cid: draft.cid,
                syncState: { ...currentSyncState, state: DraftSaveState.Updating },
              })

              const updatedDraft = await PadletApi.WishDraft.update(id, draft)

              // user may have closed composer and cleared syncState/draft
              if (state.draftByCid[draft.cid] == null) return

              await dispatch('updateDraft', { cid: draft.cid, updated_at: updatedDraft.updated_at })

              commit('SET_DRAFT_SYNC_STATE', {
                cid: draft.cid,
                syncState: { ...currentSyncState, state: DraftSaveState.Saved },
              })
            } catch (err) {
              commit('SET_DRAFT_SYNC_STATE', {
                cid: draft.cid,
                syncState: { ...currentSyncState, state: DraftSaveState.Saved },
              })
            }
          })
          break
        }
      }
    },
    debouncedSyncDraft: partitionedDebounce(
      async ({ dispatch }: { dispatch: Dispatch }, draft: DraftPost) => await dispatch('syncDraft', draft),
      {
        getKey: (_, draft) => draft.cid,
        wait: 1500,
        timers: DRAFT_SYNC_DEBOUNCE_TIMERS,
      },
    ),
  },
}

export default draft
export type { DraftState }
